import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Refresh,
  Search,
  Assignment,
  People,
  AttachMoney,
  Schedule,
  Assessment,
  TrendingUp,
} from '@mui/icons-material';
import { projectService, Project, ProjectsSummary } from '../../services/projectService';
import { RouterLink } from '../../utils/linkNav';
import { formatCurrency } from '../../utils/currency';
import TasksPage from './TasksPage';
import ResourcesPage from './ResourcesPage';
import CostTrackingPage from './CostTrackingPage';
import TimeTrackingPage from './TimeTrackingPage';
import ProjectReportsPage from './ProjectReportsPage';
import ProjectCommandCenter from './ProjectCommandCenter';
import ProjectPortfolioPage from './ProjectPortfolioPage';
import ProjectMyDayPage from './ProjectMyDayPage';
import ProjectWorkspacePage from './ProjectWorkspacePage';

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  PLANNING: 'info',
  IN_PROGRESS: 'primary',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const ProjectsList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [summary, setSummary] = useState<ProjectsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    projectName: '',
    description: '',
    startDate: '',
    endDate: '',
    estimatedBudget: '',
    status: 'PLANNING',
  });
  const [formLoading, setFormLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: any = {};
      if (filterStatus) filters.status = filterStatus;
      if (searchTerm) filters.search = searchTerm;

      const [projectsData, summaryData] = await Promise.all([
        projectService.getProjects(filters),
        projectService.getProjectsSummary(),
      ]);

      setProjects(projectsData);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchTerm]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleOpenDialog = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        projectName: project.project_name,
        description: project.description || '',
        startDate: project.start_date?.split('T')[0] || '',
        endDate: project.end_date?.split('T')[0] || '',
        estimatedBudget: project.estimated_budget?.toString() || '',
        status: project.status,
      });
    } else {
      setEditingProject(null);
      setFormData({
        projectName: '',
        description: '',
        startDate: '',
        endDate: '',
        estimatedBudget: '',
        status: 'PLANNING',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleSave = async () => {
    if (!formData.projectName.trim()) {
      setError('Project name is required');
      return;
    }

    setFormLoading(true);
    try {
      const data = {
        projectName: formData.projectName,
        description: formData.description || undefined,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        estimatedBudget: formData.estimatedBudget ? parseFloat(formData.estimatedBudget) : undefined,
        status: formData.status,
      };

      if (editingProject) {
        await projectService.updateProject(editingProject.id, data);
      } else {
        await projectService.createProject(data);
      }

      handleCloseDialog();
      fetchProjects();
    } catch (err: any) {
      setError(err.message || 'Failed to save project');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to cancel this project?')) return;

    try {
      await projectService.deleteProject(id);
      fetchProjects();
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Project Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage projects, tasks, resources, and track progress
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchProjects}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            New Project
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Assignment color="primary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {summary.projects.total_projects}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Projects
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <TrendingUp color="success" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {summary.projects.in_progress}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <AttachMoney color="info" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.projects.total_budget)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Budget
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Schedule color="warning" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {Number(summary.time.total_hours || 0).toFixed(0)}h
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hours Logged
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="PLANNING">Planning</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="ON_HOLD">On Hold</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {projects.length} projects
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Projects Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Manager</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Budget
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tasks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No projects found</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        sx={{ mt: 2 }}
                        onClick={() => handleOpenDialog()}
                      >
                        Create First Project
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow key={project.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {project.project_code}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{project.project_name}</Typography>
                        {project.customer_name && (
                          <Typography variant="caption" color="text.secondary">
                            {project.customer_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{project.project_manager_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={project.status.replace('_', ' ')}
                          size="small"
                          color={statusColors[project.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={project.progress_percent || 0}
                            sx={{ width: 60, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">{project.progress_percent || 0}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {project.estimatedBudgetFormatted || formatCurrency(project.estimated_budget)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {project.completed_tasks || 0}/{project.task_count || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to={`/projects/${project.id}/workspace`}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(project)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel Project">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(project.id)}
                            disabled={project.status === 'CANCELLED'}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Quick Navigation Cards */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
        Quick Access
      </Typography>
      <Grid container spacing={2}>
        {[
          { title: 'Tasks', icon: <Assignment />, path: '/projects/tasks', color: 'primary' },
          { title: 'Resources', icon: <People />, path: '/projects/resources', color: 'info' },
          { title: 'Cost Tracking', icon: <AttachMoney />, path: '/projects/costs', color: 'success' },
          { title: 'Time Tracking', icon: <Schedule />, path: '/projects/time', color: 'warning' },
          { title: 'Reports', icon: <Assessment />, path: '/projects/reports', color: 'error' },
        ].map((item) => (
          <Grid key={item.title} size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Card
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                py: 2,
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: 'all 0.2s' },
              }}
              onClick={() => navigate(item.path)}
            >
              <Box sx={{ color: `${item.color}.main`, mb: 1 }}>{item.icon}</Box>
              <Typography variant="body2" fontWeight="medium">
                {item.title}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Project Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProject ? 'Edit Project' : 'New Project'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.projectName}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Estimated Budget"
                type="number"
                value={formData.estimatedBudget}
                onChange={(e) => setFormData({ ...formData, estimatedBudget: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="PLANNING">Planning</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="ON_HOLD">On Hold</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={formLoading}>
            {formLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProjectsPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<ProjectCommandCenter />} />
      <Route path="all" element={<ProjectsList />} />
      <Route path="portfolio" element={<ProjectPortfolioPage />} />
      <Route path="my-day" element={<ProjectMyDayPage />} />
      <Route path=":id/workspace" element={<ProjectWorkspacePage />} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="resources" element={<ResourcesPage />} />
      <Route path="costs" element={<CostTrackingPage />} />
      <Route path="time" element={<TimeTrackingPage />} />
      <Route path="reports" element={<ProjectReportsPage />} />
    </Routes>
  );
};

export default ProjectsPage;
